import { Component, OnInit, OnDestroy, ViewEncapsulation, Input, Output, EventEmitter } from '@angular/core';
import { HomepageService } from '../../../services/homepage.service';
import { SearchService } from '../../../services/search.service';
import { StarRatingModule } from 'angular-star-rating';
import { NotifierService } from 'angular-notifier';
@Component({
  selector: 'app-recommended',
  templateUrl: './recommended.component.html',
  styleUrls: ['./recommended.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class RecommendedComponent implements OnInit, OnDestroy {
  private readonly notifier: NotifierService;
  constructor( private homepageService: HomepageService, private searchService: SearchService, notifierService: NotifierService ) {
    this.notifier = notifierService;
  }

  @Input() buId: number;
  @Input() isStar: boolean;
  @Input() editMode: boolean = false;
  @Output() showingPopular = new EventEmitter<boolean>();

  settings: any = {};
  caption: string = 'Recommended for You';
  size: number = 6;
  fallback: string = 'Hide';
  showPopularWithPrompt: boolean = false;

  // Shown to a logged-in patron who has no recommendations yet (no/insufficient circulation
  // history). Kept in one place so the wording is easy to change later; it is reused by both
  // the standalone empty state and the Popular Titles fallback.
  readonly emptyStateMessage: string = "You don't have any recent checkouts yet. Once you borrow a few titles, personalized recommendations will start appearing here.";

  isLoggedIn: boolean = false;
  loading: boolean = false;
  recommendedList: any = null;
  popularList: any = null;
  showPopular: boolean = false;
  showLoginPrompt: boolean = false;
  showEmptyState: boolean = false;
  showEditPlaceholder: boolean = false;
  private loginSub: any;

  slideConfig = {
    "slidesToShow": 6,
    'autoplay': true,
    'autoplaySpeed': 3000,
    'dots': true,
    'vertical' : false,
    'prevArrow' : '<button class="slickArrowpre slick-arrow" aria-label="Previous" type="button" style=""><i class="fas fa-chevron-left"></i></button>',
    'nextArrow' : '<button class="slickArrownxt slick-arrow" aria-label="Previous" type="button" style=""><i class="fas fa-chevron-right"></i></button>',
    'responsive': [
        {breakpoint: 1281,settings: {slidesToShow: 4} },
        {breakpoint: 992,settings: {slidesToShow: 2} },
        {breakpoint: 768,settings: {slidesToShow: 1} }
        ]
  }

  ngOnInit() {
    this.loadSettings();
    this.evaluateLoginState(localStorage.getItem('info') != null);
    // React to login being established after this widget initialized; otherwise RY
    // can stay stuck in a logged-out state while the rest of the app is logged in.
    this.loginSub = this.homepageService.isLogin$.subscribe((res: any) => {
      const loggedIn = res === 'true' || localStorage.getItem('info') != null;
      if (loggedIn && !this.isLoggedIn) {
        this.evaluateLoginState(true);
      }
    });
  }

  ngOnDestroy() {
    if (this.loginSub) { this.loginSub.unsubscribe(); }
  }

  private evaluateLoginState(isLoggedIn: boolean) {
    this.isLoggedIn = isLoggedIn;
    // reset transient view state before (re)deciding what to render
    this.showLoginPrompt = false;
    this.showEmptyState = false;
    this.showPopular = false;
    this.showEditPlaceholder = false;
    this.recommendedList = null;
    this.popularList = null;
    // Until/unless we fall back to Popular Titles below, the RY slot is not standing in for the
    // regular Popular Titles widget, so let the home page show that widget again.
    this.showingPopular.emit(false);
    if (this.isLoggedIn) {
      this.loadRecommendations();
    } else if (this.editMode) {
      this.showEditPlaceholder = true;
    } else {
      this.applyFallback();
    }
  }

  loadSettings() {
    let getwidgetsSettingDataSave = this.homepageService.getwidgetsSettingDataSave();
    if (getwidgetsSettingDataSave && getwidgetsSettingDataSave.data) {
      getwidgetsSettingDataSave.data.forEach(evt => {
        if (evt.code == 'RY' && this.buId == evt.id) {
          this.settings = evt.settings || {};
          this.size = this.settings.size || 100;
          this.fallback = this.settings.fallback || 'Hide';
          this.showPopularWithPrompt = !!this.settings.showPopularWithPrompt;
          if (evt.content && evt.content.caption) {
            this.caption = evt.content.caption;
          }
        }
      });
    }
  }

  loadRecommendations() {
    this.loading = true;
    this.homepageService.getRecommended(this.size).subscribe((data: any) => {
      let content = this.extractContent(data);
      this.loading = false;
      if (content && content.resources && content.resources.length > 0) {
        this.recommendedList = content;
      } else if (this.editMode) {
        this.showEditPlaceholder = true;
      } else {
        this.applyFallback();
      }
    }, () => {
      this.loading = false;
      if (this.editMode) {
        this.showEditPlaceholder = true;
      } else {
        this.applyFallback();
      }
    });
  }

  /* the widget content can be returned either directly or wrapped in a data/content envelope */
  extractContent(data: any) {
    if (!data) { return null; }
    if (data.data) { return data.data; }
    if (data.content) { return data.content; }
    return data;
  }

  applyFallback() {
    switch (this.fallback) {
      case 'ShowPopular':
        if (this.isLoggedIn) {
          // Logged in but no recommendations: explain why instead of prompting to log in,
          // regardless of the showPopularWithPrompt setting.
          this.showEmptyState = true;
        } else if (this.showPopularWithPrompt) {
          this.showLoginPrompt = true;
        }
        this.loadPopularFallback();
        break;
      case 'PromptLogin':
        if (this.isLoggedIn) {
          // Logged in with no recommendations: show the empty-state explanation, no login CTA.
          this.showEmptyState = true;
        } else {
          this.showLoginPrompt = true;
        }
        break;
      case 'Hide':
      default:
        // render nothing (edit mode shows a placeholder via the callers)
        break;
    }
  }

  loadPopularFallback() {
    this.showPopular = true;
    this.showingPopular.emit(true);
    this.homepageService.getPopularPage('date', true, this.size, '').subscribe((data: any) => {
      this.popularList = this.extractContent(data);
    }, () => {
      this.popularList = null;
    });
  }

  slickInit(e) {
  }

  counter(i: number) {
    return new Array(i);
  }
}
